import { useState, useEffect } from "react";
import { Box, Paper, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Pagination, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LogoSpinner from '../../components/LogoSpinner/LogoSpinner';
import EventsApi from "../../api/eventsApi/eventsApi";
import { useNavigate } from "react-router-dom";

interface Pin {
    id: string;
    name: string;
    sector: string;
    latitude: number;
    longitude: number;
    radiusInMeters: number;
    createdDate: string;
    updatedDate: string;
    userFirstName: string;
    userLastName: string;
}

const Reports = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [pins, setPins] = useState<Pin[]>([]);
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;
    const [canDeletePins, setCanDeletePins] = useState(false);
    const deleteDisabledTooltip = 'Tesis kaldırma işlemi sadece Admin yetkilerine sahip kişilerce yapılabilir. Organizasyon yöneticinize danışınız.';
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [pinToDelete, setPinToDelete] = useState<Pin | null>(null);

    const fetchPins = async () => {
        try {
            const response = await EventsApi.getPins();
            if (response && Array.isArray(response)) {
                setPins(response);
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Pin verisi alınırken hata:", error);
            setIsLoading(false);
        }
    };

    const handlePinClick = (pin: Pin) => {
        navigate(`/reports/${pin.id}`);
    };

    // PinListItem component
    const PinListItem = ({ pin, onPinClick }: { pin: Pin, onPinClick: (pin: Pin) => void }) => {
        return (
            <TableRow 
                onClick={() => onPinClick(pin)}
                sx={{ 
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    cursor: 'pointer'
                }}
            >
                <TableCell>{pin.name}</TableCell>
                <TableCell>{pin.sector}</TableCell>
                <TableCell>{new Date(pin.createdDate).toLocaleString('tr-TR')}</TableCell>
                <TableCell>{new Date(pin.updatedDate).toLocaleString('tr-TR')}</TableCell>
                <TableCell>{`${pin.userFirstName} ${pin.userLastName}`}</TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                        title={canDeletePins ? 'Tesisi sil' : deleteDisabledTooltip}
                        placement="top"
                    >
                        {/* Tooltip doesn't show on disabled elements unless wrapped */}
                        <span>
                            <IconButton
                                size="small"
                                disabled={!canDeletePins}
                                onClick={() => {
                                    if (!canDeletePins) return;
                                    setPinToDelete(pin);
                                    setDeleteDialogOpen(true);
                                }}
                                sx={{ color: canDeletePins ? '#b71c1c' : 'rgba(0,0,0,0.26)' }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </TableCell>
            </TableRow>
        );
    };

    useEffect(() => {
        fetchPins();
    }, []);

    useEffect(() => {
        const checkDeleteAccess = async () => {
            try {
                const user = await EventsApi.getUser();
                const superadminClaimId = '00000000-0000-0000-0000-000000000001';
                const adminClaimId = '00000000-0000-0000-0000-000000000002';
                setCanDeletePins(
                    user.operationClaimIds?.includes(superadminClaimId) ||
                    user.operationClaimIds?.includes(adminClaimId) ||
                    false
                );
            } catch (e) {
                console.error('Error checking pin delete access:', e);
                setCanDeletePins(false);
            }
        };
        checkDeleteAccess();
    }, []);

    const handleConfirmDelete = async () => {
        if (!pinToDelete) return;
        try {
            await EventsApi.deletePin(pinToDelete.id);
            setPins((prev) => prev.filter((p) => p.id !== pinToDelete.id));

            // keep pagination in range
            const nextCount = pins.length - 1;
            const nextTotalPages = Math.max(1, Math.ceil(nextCount / itemsPerPage));
            if (page > nextTotalPages) setPage(nextTotalPages);
        } catch (e) {
            console.error('Pin delete error:', e);
            alert('Tesis silinemedi. Yetkiniz olmayabilir.');
        } finally {
            setDeleteDialogOpen(false);
            setPinToDelete(null);
        }
    };

    // Pagination hesaplamaları
    const totalPages = Math.ceil(pins.length / itemsPerPage);
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedPins = pins.slice(startIndex, endIndex);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        // Sayfa değiştiğinde tablonun üstüne scroll et
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (isLoading) {
        return (
            <LogoSpinner size={60} minHeight="100vh" />
        );
    }

    return (
        <Box sx={{ 
            flexGrow: 1, 
            padding: '1rem',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <Paper elevation={3} sx={{ 
                height: '100%', 
                padding: '1rem', 
                display: 'flex',
                flexDirection: 'column', 
                alignItems: 'center' 
            }}>
                <Typography variant="h5" gutterBottom>
                    Tesislerim
                </Typography>
                <TableContainer sx={{ maxHeight: '90%', overflow: 'auto', width: '100%', mb: 2 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    Pin Adı
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    Sektör
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    Oluşturulma Tarihi
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    Güncelleme Tarihi
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    Son Güncelleyen
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center">
                                    Sil
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedPins.length > 0 ? (
                                paginatedPins.map((pin) => (
                                    <PinListItem 
                                        key={pin.id} 
                                        pin={pin} 
                                        onPinClick={handlePinClick}
                                    />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">
                                            Tesis bulunamadı
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pins.length > itemsPerPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                        <Pagination 
                            count={totalPages} 
                            page={page} 
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton 
                            showLastButton
                        />
                    </Box>
                )}
            </Paper>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setPinToDelete(null);
                }}
            >
                <DialogTitle>Tesis Sil</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 1 }}>
                        <b>{pinToDelete?.name}</b> tesisini silmek istediğinize emin misiniz?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setPinToDelete(null);
                        }}
                    >
                        Vazgeç
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDelete}
                        sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Reports; 